import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import { accountsById, mapDiscrepancyFromApi } from './bankingApiMappers';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import { CustomSelect } from '../../shared/components/custom';
import {
  ExclamationCircleIcon,
  BuildingLibraryIcon,
  DocumentArrowDownIcon,
  DocumentMagnifyingGlassIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const STATUS_KEYS = {
  unresolved: 'status_unresolved',
  analysis: 'status_analysis',
  resolved: 'status_resolved',
};

const TYPE_KEYS = {
  missing_bank: 'type_missing_bank',
  missing_accounting: 'type_missing_accounting',
  different_amounts: 'type_different_amounts',
};

const DiscrepanciesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.discrepanciesPage.${key}`, opts);
  const fmt = (value) => formatBrandMoney(value);
  const dateLocale = getBrandLocale(i18n.language);

  const [selectedAccount, setSelectedAccount] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDiscrepancy, setExpandedDiscrepancy] = useState(null);
  const [showBulkResolveModal, setShowBulkResolveModal] = useState(false);
  const [selectedDiscrepancies, setSelectedDiscrepancies] = useState([]);
  const [resolutionNote, setResolutionNote] = useState('');
  const [isBulkResolving, setIsBulkResolving] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const statusLabel = (status) => p(STATUS_KEYS[status] || status);
  const typeLabel = (type) => p(TYPE_KEYS[type] || type);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsData, discrepanciesData, statsData] = await Promise.all([
        localApiAdapter.get('/banking/accounts'),
        localApiAdapter.get('/banking/discrepancies'),
        localApiAdapter.get('/banking/discrepancies/stats/summary').catch(() => null),
      ]);
      const accountList = normalizeAndSyncBankAccounts(accountsData);
      const byId = accountsById(accountList);
      setAccounts(accountList);
      setDiscrepancies(
        (Array.isArray(discrepanciesData) ? discrepanciesData : []).map((row) =>
          mapDiscrepancyFromApi(row, byId),
        ),
      );
      setSummaryStats(statsData && typeof statsData === 'object' ? statsData : null);
    } catch (error) {
      console.error('Error loading discrepancies:', error);
      toast.error(error.message || p('toast_bulk_error'));
      setDiscrepancies([]);
    } finally {
      setLoading(false);
    }
  }, [p]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const accountOptions = useMemo(
    () => [
      { value: '', label: p('filter_all_accounts') },
      ...accounts.map((account) => ({
        value: String(account.id),
        label: `${account.bank_name || ''} — ${account.account_name || account.account_number || account.id}`.trim(),
      })),
    ],
    [accounts, p],
  );

  const filteredDiscrepancies = discrepancies.filter((d) => {
    if (selectedAccount && String(d.accountId) !== String(selectedAccount)) return false;
    if (typeFilter !== 'all' && d.type !== typeFilter) return false;
    if (statusFilter !== 'all' && d.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !d.description?.toLowerCase().includes(term) &&
        !d.bank?.toLowerCase().includes(term) &&
        !d.accountNumber?.toLowerCase().includes(term) &&
        !String(d.amount).includes(term)
      ) return false;
    }
    return true;
  });

  const stats = useMemo(() => {
    const total = summaryStats?.totalDiscrepancies ?? discrepancies.length;
    const unresolved = discrepancies.filter((d) => d.status === 'unresolved').length;
    const inAnalysis = discrepancies.filter((d) => d.status === 'analysis').length;
    const resolved = discrepancies.filter((d) => d.status === 'resolved').length;
    return [
      { titleKey: 'stat_total', value: String(total), icon: DocumentMagnifyingGlassIcon, color: 'from-blue-500 to-blue-600' },
      { titleKey: 'stat_unresolved', value: String(unresolved), icon: ExclamationCircleIcon, color: 'from-red-500 to-red-600' },
      { titleKey: 'stat_in_analysis', value: String(inAnalysis), icon: ClockIcon, color: 'from-amber-500 to-amber-600' },
      { titleKey: 'stat_resolved', value: String(resolved), icon: CheckCircleIcon, color: 'from-green-500 to-green-600' },
      { titleKey: 'stat_monitored_accounts', value: String(accounts.length), icon: BuildingLibraryIcon, color: 'from-purple-500 to-purple-600' },
    ];
  }, [accounts.length, discrepancies, summaryStats]);

  const toggleDiscrepancy = (id) => {
    setExpandedDiscrepancy(expandedDiscrepancy === id ? null : id);
  };

  const resolveDiscrepanciesBulk = async () => {
    setIsBulkResolving(true);
    try {
      await Promise.all(
        selectedDiscrepancies.map((id) =>
          localApiAdapter.patch(`/banking/discrepancies/${id}/resolve`, {
            resolution_notes: resolutionNote || null,
          }),
        ),
      );
      toast.success(p('toast_bulk_success'));
      setShowBulkResolveModal(false);
      setSelectedDiscrepancies([]);
      setResolutionNote('');
      await loadData();
    } catch (error) {
      toast.error(error.message || p('toast_bulk_error'));
      console.error('Error:', error);
    } finally {
      setIsBulkResolving(false);
    }
  };

  const handleBulkResolve = () => {
    if (selectedDiscrepancies.length === 0) {
      const unresolvedDiscrepancies = discrepancies.filter((d) => d.status === 'unresolved');
      setSelectedDiscrepancies(unresolvedDiscrepancies.map((d) => d.id));
    }
    setShowBulkResolveModal(true);
  };

  const resolveDiscrepancy = async (discrepancyId) => {
    setResolvingId(discrepancyId);
    try {
      await localApiAdapter.patch(`/banking/discrepancies/${discrepancyId}/resolve`, {
        resolution_notes: null,
      });
      toast.success(p('toast_resolve_success'));
      await loadData();
    } catch (error) {
      toast.error(error.message || p('toast_resolve_error'));
      console.error('Error:', error);
    } finally {
      setResolvingId(null);
    }
  };

  const analyzeDiscrepancy = async (discrepancyId) => {
    setAnalyzingId(discrepancyId);
    try {
      toast.success(p('toast_analyze_success'));
      setExpandedDiscrepancy(discrepancyId);
    } catch (error) {
      toast.error(error.message || p('toast_analyze_error'));
      console.error('Error:', error);
    } finally {
      setAnalyzingId(null);
    }
  };

  const getStatusStyles = (status) => {
    if (status === 'unresolved') {
      return {
        icon: 'bg-gradient-to-r from-red-500 to-red-600 text-white',
        badge: 'bg-red-500/20 text-red-400',
      };
    }
    if (status === 'analysis') {
      return {
        icon: 'bg-gradient-to-r from-amber-500 to-amber-600 text-white',
        badge: 'bg-amber-500/20 text-amber-400',
      };
    }
    return {
      icon: 'bg-gradient-to-r from-green-500 to-green-600 text-white',
      badge: 'bg-green-500/20 text-green-400',
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">{p('title')}</h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <button
          onClick={handleBulkResolve}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
        >
          <CheckCircleIcon className="h-5 w-5" />
          <span>{p('resolve_button')}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.titleKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.titleKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                  <stat.icon className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-lg font-semibold text-white mb-4">{p('filters_title')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div>
            <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_account')}</label>
            <CustomSelect
              value={selectedAccount}
              onChange={setSelectedAccount}
              options={accountOptions}
              searchable
              placeholder={p('filter_all_accounts')}
            />
          </div>

          <div>
            <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_discrepancy_type')}</label>
            <select
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">{p('filter_all')}</option>
              <option value="missing_bank">{p('type_missing_bank')}</option>
              <option value="missing_accounting">{p('type_missing_accounting')}</option>
              <option value="different_amounts">{p('type_different_amounts')}</option>
            </select>
          </div>

          <div>
            <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_status')}</label>
            <select
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{p('filter_all')}</option>
              <option value="unresolved">{p('status_unresolved')}</option>
              <option value="analysis">{p('status_analysis')}</option>
              <option value="resolved">{p('status_resolved')}</option>
            </select>
          </div>

          <div>
            <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('search_label')}</label>
            <div className="relative">
              <input
                type="text"
                placeholder={p('search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-white/50" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">{p('list_title', { count: filteredDiscrepancies.length })}</h2>
          {loading ? <span className="text-white/50 text-sm">{t('common.loading', { defaultValue: 'جاري التحميل…' })}</span> : null}
        </div>

        <div className="p-6 space-y-4">
          {filteredDiscrepancies.length > 0 ? (
            filteredDiscrepancies.map((discrepancy) => {
              const statusStyles = getStatusStyles(discrepancy.status);
              return (
                <motion.div
                  key={discrepancy.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white/5 rounded-xl border border-white/10"
                >
                  <div
                    onClick={() => toggleDiscrepancy(discrepancy.id)}
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors rounded-t-xl"
                  >
                    <div className="flex items-center">
                      <div className={`p-2 rounded-xl mr-4 ${statusStyles.icon}`}>
                        <ExclamationCircleIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="flex items-center">
                          <span className="text-white font-medium">{typeLabel(discrepancy.type)}</span>
                          <span className="mx-2 text-white/30">·</span>
                          <span className="text-white/70">{discrepancy.date ? new Date(discrepancy.date).toLocaleDateString(dateLocale) : '—'}</span>
                        </div>
                        <div className="mt-1 flex items-center">
                          <div className="flex items-center text-sm text-white/60">
                            <BuildingLibraryIcon className="h-4 w-4 mr-1" />
                            <span>{discrepancy.bank} - {discrepancy.accountNumber}</span>
                          </div>
                          <span className="mx-2 text-white/30">·</span>
                          <span className="text-sm font-medium text-white">{fmt(discrepancy.amount)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className={`px-2 py-1 text-xs rounded-full mr-3 ${statusStyles.badge}`}>
                        {statusLabel(discrepancy.status)}
                      </span>
                      {expandedDiscrepancy === discrepancy.id ? (
                        <ChevronUpIcon className="h-5 w-5 text-white/70" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-white/70" />
                      )}
                    </div>
                  </div>

                  {expandedDiscrepancy === discrepancy.id && (
                    <div className="p-4 bg-white/5 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                          <h3 className="text-sm font-medium text-white/70 flex items-center mb-3">
                            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                            {p('section_bank_movement')}
                          </h3>
                          {discrepancy.bankEntry ? (
                            <div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_reference')}</span>
                                <div className="text-white">{discrepancy.bankEntry.reference}</div>
                              </div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_date')}</span>
                                <div className="text-white">{discrepancy.bankEntry.date ? new Date(discrepancy.bankEntry.date).toLocaleDateString(dateLocale) : '—'}</div>
                              </div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_description')}</span>
                                <div className="text-white">{discrepancy.bankEntry.description}</div>
                              </div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_amount')}</span>
                                <div className="text-white font-medium">{fmt(discrepancy.bankEntry.amount)}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <p className="text-red-400">{p('missing_bank_movement')}</p>
                            </div>
                          )}
                        </div>

                        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                          <h3 className="text-sm font-medium text-white/70 flex items-center mb-3">
                            <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-1" />
                            {p('section_accounting_entry')}
                          </h3>
                          {discrepancy.accountingEntry ? (
                            <div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_reference')}</span>
                                <div className="text-white">{discrepancy.accountingEntry.reference}</div>
                              </div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_date')}</span>
                                <div className="text-white">{discrepancy.accountingEntry.date ? new Date(discrepancy.accountingEntry.date).toLocaleDateString(dateLocale) : '—'}</div>
                              </div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_description')}</span>
                                <div className="text-white">{discrepancy.accountingEntry.description}</div>
                              </div>
                              <div className="mb-2">
                                <span className="text-xs text-white/50">{p('label_amount')}</span>
                                <div className="text-white font-medium">{fmt(discrepancy.accountingEntry.amount)}</div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <p className="text-red-400">{p('missing_accounting_entry')}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end space-x-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); resolveDiscrepancy(discrepancy.id); }}
                          disabled={resolvingId === discrepancy.id}
                          className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {p('mark_resolved')}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); analyzeDiscrepancy(discrepancy.id); }}
                          disabled={analyzingId === discrepancy.id}
                          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-lg transition-all duration-300 disabled:opacity-50"
                        >
                          {p('analyze')}
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })
          ) : (
            <div className="p-8 text-center">
              <p className="text-white/60">{loading ? t('common.loading', { defaultValue: 'جاري التحميل…' }) : p('empty')}</p>
            </div>
          )}
        </div>
      </div>

      {showBulkResolveModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBulkResolveModal(false)} role="presentation" />

          <div className="bg-slate-800 rounded-3xl border border-white/20 p-8 w-full max-w-lg z-10 relative">
            <button
              className="absolute right-4 top-4 text-white/50 hover:text-white"
              onClick={() => setShowBulkResolveModal(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            <h2 className="text-2xl font-bold text-white mb-6">{p('bulk_modal_title')}</h2>

            <p className="text-white/70 mb-4">
              {p('bulk_modal_message', { count: selectedDiscrepancies.length })}
            </p>

            <div className="mb-4">
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('resolution_note_label')}</label>
              <textarea
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={p('resolution_note_placeholder')}
                rows={4}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkResolveModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors"
              >
                {p('cancel')}
              </button>
              <button
                onClick={resolveDiscrepanciesBulk}
                disabled={isBulkResolving}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-lg transition-all duration-300 flex items-center space-x-2 disabled:opacity-50"
              >
                {isBulkResolving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full" />
                    <span>{p('processing')}</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4" />
                    <span>{p('confirm_resolution')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DiscrepanciesPage;
